/**
 * Captions tab — the essentials view for `selection.el === "caption"`.
 *
 * Order is deliberate: in the Studio the WORDS come first (the cue editor
 * when a cue is selected, the transcript list when none is), because someone
 * who clicked a caption is at least as likely to be fixing what it says as
 * how it looks. Brand Setup has no transcript and no words to fix — its
 * scope is "your brand's look" — so it starts at Size.
 */
import { useMemo } from "react";

import type { CaptionPatch, EditPatch } from "../../../../../src/lib/clipEditState.ts";
import type { SerializableCue } from "../../../../../src/remotion/types.ts";
import type { StudioSelection } from "../../../lib/studioTypes.ts";
import { StudioInspector, type StudioInspectorProps } from "../../StudioInspector.tsx";
import { ColorRow, FontRow, SegmentedRow, SliderRow } from "../essentials/index.ts";
import { positionPatchFor } from "../../../lib/brandSetup/geometry.ts";
import { TranscriptList } from "./TranscriptList.tsx";

export function CaptionTab({ host, inspector, selection, cues, onSelectCue, query, onQueryChange, onOpenMore }: {
  host: "brand-setup" | "studio";
  inspector: StudioInspectorProps;
  selection: StudioSelection;
  cues: SerializableCue[];
  onSelectCue: (i: number) => void;
  query: string;
  onQueryChange: (q: string) => void;
  /** "the real control is in More options" — the panel owns that state. */
  onOpenMore: () => void;
}): React.JSX.Element {
  const { layout, brand, onPreview, onCommit } = inspector;
  const cap = layout.caption;
  const capPatch = (v: CaptionPatch): EditPatch => ({ layout: { caption: v } });
  const lowered = cap.justify === "flex-end";
  const capStyle = cap.captionStyle ?? "punch";
  const dir = (brand.direction ?? "rtl") === "rtl" ? "rtl" : "ltr";

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return cues.map((c, i) => ({ c, i })).filter(({ c }) => !q || c.text.toLowerCase().includes(q));
  }, [cues, query]);

  // With ONE caption selected the tab holds two scopes: the cue editor
  // (this caption) and everything else (all captions). Two headers keep the
  // operator from reading "Size" as a per-caption knob — one caption has no
  // size of its own; the honest per-caption moves are text, break and split.
  const oneCue = host === "studio" && selection.cueIdx != null;
  const scopeHeader = (text: string): React.JSX.Element => (
    <div
      style={{
        fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em",
        color: "var(--ql-fg-muted)", margin: "6px 0 2px",
      }}
    >
      {text}
    </div>
  );

  return (
    <>
      {oneCue && scopeHeader("This caption")}
      {/* Mounted through the inspector, never re-implemented. */}
      {host === "studio" && selection.cueIdx != null && (
        <StudioInspector {...inspector} selection={selection} variant="cue-only" />
      )}
      {oneCue && scopeHeader("All captions")}
      {host === "studio" && selection.cueIdx == null && cues.length > 0 && (
        <TranscriptList
          matches={matches}
          query={query}
          onQueryChange={onQueryChange}
          selectedIdx={selection.cueIdx}
          onSelectCue={onSelectCue}
          dir={dir}
        />
      )}
      <SliderRow
        label="Size" value={cap.fontSizePx} min={40} max={320} step={5} unit="px"
        toPatch={(v) => capPatch({ fontSizePx: v })} commitLabel="Caption size"
        onPreview={onPreview} onCommit={onCommit}
      />
      <SegmentedRow
        label="Lines"
        value={cap.maxLines == null ? "auto" : String(cap.maxLines)}
        options={[
          { id: "auto", label: "Let us decide" },
          { id: "1", label: "1" },
          { id: "2", label: "2" },
          { id: "3", label: "3" },
        ]}
        toPatch={(v) => capPatch({ maxLines: v === "auto" ? null : (Number(v) as 1 | 2 | 3) })}
        commitLabel="Caption lines"
        onPreview={onPreview} onCommit={onCommit}
      />
      <SegmentedRow
        label="Position"
        value={lowered ? "lower" : "middle"}
        options={[{ id: "middle", label: "Middle" }, { id: "lower", label: "Lower" }]}
        // Two fields move together on purpose: "Lower" with the middle's
        // bottomPx still renders mid-frame, which reads as a dead control.
        // …through the SAME function Brand Setup's Position step writes, so
        // "Lower" means one distance in the wizard and in the editor. It is
        // direction-aware: the founding RTL caption sits at its own 240 px,
        // and deriving it from the size there would move an Arabic brand's
        // captions the moment someone clicked the card they were already on.
        toPatch={(v) => capPatch(positionPatchFor(dir, v === "lower" ? "lower" : "middle", cap.fontSizePx)!)}
        commitLabel="Caption position"
        onPreview={onPreview} onCommit={onCommit}
      />
      {lowered && (
        <SliderRow
          label="Space from the bottom" value={cap.bottomPx} min={0} max={1200} step={10} unit="px"
          toPatch={(v) => capPatch({ bottomPx: v })} commitLabel="Caption bottom space"
          onPreview={onPreview} onCommit={onCommit}
        />
      )}
      <FontRow
        label="Caption font" value={brand.captionFont} families={inspector.fontFamilies}
        onPick={(f) => onCommit({ brand: { captionFont: f } }, `Caption font → ${f}`)}
      />
      <ColorRow
        label="Text color" value={brand.captionColor}
        toPatch={(hex) => ({ brand: { captionColor: hex } })} commitLabel="Text color"
        onPreview={onPreview} onCommit={onCommit}
      />
      <ColorRow
        label="Highlight color" value={brand.captionAccent}
        toPatch={(hex) => ({ brand: { captionAccent: hex } })} commitLabel="Highlight color"
        onPreview={onPreview} onCommit={onCommit}
      />
      <SegmentedRow
        label="Style"
        // A punch-layout clip (two hand-tuned line sizes) is neither of the
        // two uniform choices — it shows as its own current value rather
        // than lighting a segment the clip is not on.
        value={capStyle === "punch" ? "punch" : (cap.accentMode ?? "spans") === "line-punch" ? "line-punch" : "uniform"}
        options={[
          ...(capStyle === "punch" ? [{ id: "punch", label: "Punch line" }] : []),
          { id: "uniform", label: "Uniform" },
          { id: "line-punch", label: "Highlight the last line" },
        ]}
        toPatch={(v) => capPatch({ captionStyle: "uniform", accentMode: v === "line-punch" ? "line-punch" : "spans" })}
        commitLabel="Style"
        onPreview={onPreview} onCommit={onCommit}
      />
      {/* Last-line size (spec 2026-09-16 round 2 §2.6) — only where it can
          act: uniform captions with more than one row. The punch layout has
          its own two sizes (More options); a one-line cap has no last row. */}
      {capStyle !== "punch" && cap.maxLines !== 1 && (
        <SliderRow
          label={cap.maxLines === 2 ? "Second line size" : "Last line size"}
          value={Math.round((cap.lastLineScale ?? 1) * 100)} min={60} max={180} step={5} unit="%"
          toPatch={(v) => capPatch({ lastLineScale: v / 100 })}
          commitLabel={cap.maxLines === 2 ? "Second line size" : "Last line size"}
          onPreview={onPreview} onCommit={onCommit}
        />
      )}
      {capStyle === "punch" && (
        <button
          type="button"
          onClick={onOpenMore}
          style={{
            textAlign: "start", padding: 0, border: "none", background: "transparent", cursor: "pointer",
            fontFamily: "inherit", fontSize: 11, lineHeight: 1.5, color: "var(--ql-fg-subtle)",
            textDecoration: "underline", textUnderlineOffset: 2,
          }}
        >
          This clip uses the punch layout — its two line sizes live in More options.
        </button>
      )}
    </>
  );
}
